import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { describeValue, type PendingChange } from './settings-changes';
import { useUnsavedChanges } from './unsaved-changes';

/**
 * The one save bar every settings form uses (DEV-133).
 *
 * Shows how many changes are unsaved, offers Review (before -> after per
 * field, credentials as "changed" only), Discard and Save. A change that needs
 * a reason (client-money or revenue account, a credential, LIVE) is saved
 * from the review, which asks for the reason first; the API refuses it
 * without one. While anything is unsaved, leaving the page warns.
 */

interface Props {
  readonly changes: readonly PendingChange[];
  readonly saving: boolean;
  /** Save; `reason` is set when a change needs one. Resolve/reject is the caller's mutation. */
  readonly onSave: (reason?: string) => void;
  readonly onDiscard: () => void;
  /** Stuck to the bottom of the screen (a page with one form) or inline (a card). */
  readonly sticky?: boolean;
  /** A save error to show inside the review, so it is not hidden behind it. */
  readonly error?: string;
  /** Closes the review once a save has succeeded. */
  readonly savedAt?: number;
  /** The shortest reason the server accepts for this section (DEV-137). */
  readonly reasonMinLength?: number;
  /** Why these changes cannot be saved yet; Save stays off while it is set. */
  readonly blocked?: string;
  /** What the reason prompt says, when the section's rule is not the money one. */
  readonly reasonPrompt?: string;
  /** A consequence the admin must see before saving (DEV-138: the commission recipient changes). */
  readonly warning?: string;
  /**
   * DEV-142 (restore): what the bar says instead of "N unsaved changes", the
   * Save button's name, and the review's title.
   */
  readonly summary?: string;
  readonly saveLabel?: string;
  readonly reviewTitle?: string;
  /** DEV-142: a box the admin must tick before Save, e.g. a restore that switches the transfer to LIVE. */
  readonly confirmLabel?: string;
  /** DEV-142: Discard's name (a restore is cancelled, not discarded). */
  readonly discardLabel?: string;
}

export function SettingsSaveBar({
  changes, saving, onSave, onDiscard, sticky = false, error, savedAt, reasonMinLength = 1, blocked, reasonPrompt, warning,
  summary, saveLabel = 'Save', reviewTitle = 'Review changes', confirmLabel, discardLabel = 'Discard',
}: Props): JSX.Element | null {
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const needsReason = changes.some((c) => c.needsReason);
  const reasonTooShort = needsReason && reason.trim().length < reasonMinLength;
  const unconfirmed = confirmLabel !== undefined && !confirmed;
  useUnsavedChanges(changes.length > 0);

  // Saved, or nothing left to save (Discard): the review closes and its
  // reason is dropped, so it can never be attached to a later change.
  const nothingPending = changes.length === 0;
  useEffect(() => {
    setReviewing(false);
    setReason('');
    setConfirmed(false);
  }, [savedAt, nothingPending]);

  if (changes.length === 0) return null;
  const count = summary ?? `${String(changes.length)} unsaved change${changes.length === 1 ? '' : 's'}`;
  const opensReview = needsReason || confirmLabel !== undefined;

  return (
    <>
      <Paper
        elevation={sticky ? 6 : 0}
        variant={sticky ? 'elevation' : 'outlined'}
        role="region"
        aria-label="Unsaved changes"
        sx={{ p: 1.5, mt: 2, ...(sticky ? { position: 'sticky', bottom: 16, zIndex: 2 } : {}) }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
          {needsReason ? <Chip size="small" color="warning" variant="outlined" label="Needs a reason" /> : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onDiscard} disabled={saving}>{discardLabel}</Button>
          <Button variant="outlined" onClick={() => setReviewing(true)} disabled={saving}>Review</Button>
          <Button
            variant="contained"
            onClick={() => (opensReview ? setReviewing(true) : onSave())}
            disabled={saving || blocked !== undefined}
          >
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </Stack>
        {!reviewing && warning !== undefined ? <Alert severity="warning" sx={{ mt: 1.5 }}>{warning}</Alert> : null}
        {!reviewing && blocked !== undefined ? <Alert severity="warning" sx={{ mt: 1.5 }}>{blocked}</Alert> : null}
        {!reviewing && error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
      </Paper>

      <Dialog open={reviewing} onClose={() => setReviewing(false)} fullWidth maxWidth="md" aria-labelledby="settings-review-title">
        <DialogTitle id="settings-review-title">{reviewTitle}</DialogTitle>
        <DialogContent>
          <Table size="small" aria-label="Changes to save">
            <TableHead>
              <TableRow>
                <TableCell>Setting</TableCell>
                <TableCell>Now</TableCell>
                <TableCell>After saving</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((change) => (
                <TableRow key={change.key}>
                  <TableCell>
                    {change.label}
                    {change.needsReason ? <Chip size="small" color="warning" variant="outlined" label="needs a reason" sx={{ ml: 1 }} /> : null}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{change.secret ? 'stored (hidden)' : describeValue(change.before)}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{change.secret ? 'changed (hidden)' : describeValue(change.after)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {warning !== undefined ? <Alert severity="warning" sx={{ mt: 2 }}>{warning}</Alert> : null}
          {needsReason ? (
            <>
              <DialogContentText sx={{ mt: 2, mb: 1 }}>
                {reasonPrompt ?? 'These changes can redirect money or change who can act, so the reason is kept in the audit log.'}
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                inputProps={{ name: 'nvx-settings-change-reason', autoComplete: 'off', maxLength: 2000 }}
                {...(reasonMinLength > 1 ? { helperText: `At least ${String(reasonMinLength)} characters. Kept in the History.` } : {})}
              />
            </>
          ) : null}
          {confirmLabel !== undefined ? (
            <FormControlLabel
              sx={{ mt: 2, display: 'flex' }}
              control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
              label={confirmLabel}
            />
          ) : null}
          {blocked !== undefined ? <Alert severity="warning" sx={{ mt: 2 }}>{blocked}</Alert> : null}
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewing(false)}>Back</Button>
          <Button
            variant="contained"
            color={needsReason ? 'warning' : 'primary'}
            disabled={saving || blocked !== undefined || reasonTooShort || unconfirmed}
            onClick={() => onSave(needsReason ? reason.trim() : undefined)}
          >
            {saving ? 'Saving…' : saveLabel !== 'Save' ? `${saveLabel} with reason` : needsReason ? 'Save with reason' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
